import React, { useCallback, useRef, useState } from 'react';
import { Box, Stack, Button, IconButton, Tooltip, Typography, TextField } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import {
  SpreadsheetColumn,
  SpreadsheetRow,
  newSpreadsheetId,
} from '../../models/SpreadsheetDocument';
import { useColumnResize } from '../../hooks/useColumnResize';

const INDEX_COL_WIDTH = 36;
const ACTION_COL_WIDTH = 40;
const DEFAULT_COL_WIDTH = 140;
const MIN_COL_WIDTH = 60;

/** 오늘 날짜 YYYY-MM-DD (로컬 시각 기준). */
export function todayStamp(): string {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

interface SpreadsheetGridProps {
  columns: SpreadsheetColumn[];
  rows: SpreadsheetRow[];
  /** false 면 읽기 전용(편집 input/툴바/삭제 숨김). */
  writable: boolean;
  /**
   * 지정하면 이 이름의 컬럼에 수정일이 자동 기입된다(계정 정보의 "업데이트 날짜").
   * 같은 행의 다른 셀을 고칠 때 그 행만 갱신하며, 날짜 칸을 직접 고칠 때는
   * 사용자가 적은 값을 덮어쓰지 않는다. 해당 이름의 컬럼이 없으면 아무 일도 안 한다.
   */
  stampColumnName?: string;
  onColumnsChange: (columns: SpreadsheetColumn[]) => void;
  onRowsChange: (rows: SpreadsheetRow[]) => void;
}

/**
 * 엑셀 스타일 편집 그리드 (단일 문서).
 *  - 셀 inline 편집, Enter 로 같은 컬럼 다음 행 이동
 *  - 컬럼 추가/이름변경(더블클릭)/삭제/폭 드래그
 *  - 행 추가/삭제
 *
 * 상태(컬럼/행)는 부모가 소유 — 변경은 onColumnsChange/onRowsChange 로 위임한다.
 * 키보드 네비게이션은 이 그리드 컨테이너 내부로 한정(여러 문서가 동시에 떠도 안 섞임).
 */
const SpreadsheetGrid: React.FC<SpreadsheetGridProps> = ({
  columns,
  rows,
  writable,
  stampColumnName,
  onColumnsChange,
  onRowsChange,
}) => {
  const [editingHeaderId, setEditingHeaderId] = useState<string | null>(null);
  /** 마지막으로 정렬한 컬럼·방향 — 같은 버튼을 다시 누르면 역순으로 뒤집는다. */
  const [sort, setSort] = useState<{ colId: string; dir: 'asc' | 'desc' } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleResizeColumn = useCallback(
    (colId: string, width: number) => {
      onColumnsChange(
        columns.map((c) => (c.id === colId ? { ...c, width: Math.round(width) } : c)),
      );
    },
    [columns, onColumnsChange],
  );

  const { draftWidths: resizeDrafts, startResize } = useColumnResize<string>(handleResizeColumn, {
    minWidth: MIN_COL_WIDTH,
  });

  const effectiveColWidth = (col: SpreadsheetColumn): number =>
    resizeDrafts[col.id] ?? col.width ?? DEFAULT_COL_WIDTH;

  const handleAddColumn = useCallback(() => {
    const newCol: SpreadsheetColumn = {
      id: newSpreadsheetId('col'),
      name: `새 컬럼 ${columns.length + 1}`,
      width: 160,
    };
    onColumnsChange([...columns, newCol]);
    setEditingHeaderId(newCol.id);
  }, [columns, onColumnsChange]);

  const handleRenameColumn = useCallback(
    (colId: string, name: string) => {
      onColumnsChange(columns.map((c) => (c.id === colId ? { ...c, name } : c)));
    },
    [columns, onColumnsChange],
  );

  const handleDeleteColumn = useCallback(
    (colId: string) => {
      if (columns.length <= 1) {
        window.alert('최소 1개 컬럼은 남겨야 합니다.');
        return;
      }
      const colName = columns.find((c) => c.id === colId)?.name ?? '컬럼';
      if (!window.confirm(`'${colName}' 컬럼을 삭제할까요? 해당 컬럼의 모든 셀 값이 함께 삭제됩니다.`)) {
        return;
      }
      onColumnsChange(columns.filter((c) => c.id !== colId));
      onRowsChange(
        rows.map((r) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [colId]: _drop, ...rest } = r.cells;
          return { ...r, cells: rest };
        }),
      );
    },
    [columns, rows, onColumnsChange, onRowsChange],
  );

  const handleAddRow = useCallback(() => {
    onRowsChange([...rows, { id: newSpreadsheetId('row'), cells: {} }]);
  }, [rows, onRowsChange]);

  const handleDeleteRow = useCallback(
    (rowId: string) => {
      onRowsChange(rows.filter((r) => r.id !== rowId));
    },
    [rows, onRowsChange],
  );

  const handleCellChange = useCallback(
    (rowId: string, colId: string, value: string) => {
      // 수정일 자동 기입: 날짜 칸 자신을 고칠 때는 사용자가 적은 값을 그대로 둔다.
      const stampCol = stampColumnName
        ? columns.find((c) => c.name.trim() === stampColumnName)
        : undefined;
      const stamp = stampCol && stampCol.id !== colId ? todayStamp() : undefined;
      onRowsChange(
        rows.map((r) =>
          r.id !== rowId
            ? r
            : {
                ...r,
                cells: {
                  ...r.cells,
                  [colId]: value,
                  ...(stamp ? { [stampCol!.id]: stamp } : {}),
                },
              },
        ),
      );
    },
    [rows, columns, stampColumnName, onRowsChange],
  );

  /**
   * 컬럼 기준 행 정렬. 빈 칸은 방향과 무관하게 항상 아래로 보낸다(빈 행이 위로 떠오르면
   * 표를 읽기 어렵다). 숫자·B1/B10 같은 값이 섞이므로 numeric 비교를 쓴다.
   *
   * 정렬이 안정적(Array.prototype.sort)이라 품목 → 층 순서로 두 번 누르면
   * 층별로 묶이고 그 안은 품목순이 유지된다.
   */
  const handleSortByColumn = useCallback(
    (colId: string) => {
      const dir = sort?.colId === colId && sort.dir === 'asc' ? 'desc' : 'asc';
      const sorted = [...rows].sort((a, b) => {
        const av = (a.cells[colId] ?? '').trim();
        const bv = (b.cells[colId] ?? '').trim();
        if (!av && !bv) return 0;
        if (!av) return 1;
        if (!bv) return -1;
        const cmp = av.localeCompare(bv, 'ko', { numeric: true });
        return dir === 'asc' ? cmp : -cmp;
      });
      setSort({ colId, dir });
      onRowsChange(sorted);
    },
    [rows, sort, onRowsChange],
  );

  const handleCellKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, rowIdx: number, colId: string) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      const nextRow = rows[rowIdx + 1];
      if (!nextRow) return;
      const nextEl = containerRef.current?.querySelector<HTMLInputElement>(
        `input[data-cell="${nextRow.id}__${colId}"]`,
      );
      nextEl?.focus();
      nextEl?.select();
    },
    [rows],
  );

  const tableWidth =
    INDEX_COL_WIDTH +
    columns.reduce((sum, c) => sum + effectiveColWidth(c), 0) +
    (writable ? ACTION_COL_WIDTH : 0);

  return (
    <Box>
      {writable && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddColumn}>
            컬럼 추가
          </Button>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddRow}>
            행 추가
          </Button>
        </Stack>
      )}

      <Box
        ref={containerRef}
        sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
      >
        <Box
          component="table"
          sx={{
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
            width: `${tableWidth}px`,
            '& th, & td': { border: '1px solid', borderColor: 'divider', p: 0, verticalAlign: 'middle' },
          }}
        >
          <thead>
            <Box component="tr" sx={{ bgcolor: 'action.hover' }}>
              <Box
                component="th"
                sx={{ width: INDEX_COL_WIDTH, py: 0.5, color: 'text.secondary', fontSize: 11, fontWeight: 600 }}
              >
                #
              </Box>
              {columns.map((col) => {
                const w = effectiveColWidth(col);
                const isResizing = resizeDrafts[col.id] !== undefined;
                return (
                  <Box
                    component="th"
                    key={col.id}
                    sx={{ width: w, py: 0.5, px: 0.5, bgcolor: 'action.hover', position: 'relative' }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
                      {writable && editingHeaderId === col.id ? (
                        <TextField
                          autoFocus
                          size="small"
                          value={col.name}
                          onChange={(e) => handleRenameColumn(col.id, e.target.value)}
                          onBlur={() => setEditingHeaderId(null)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === 'Escape') {
                              e.preventDefault();
                              setEditingHeaderId(null);
                            }
                          }}
                          variant="standard"
                          sx={{ flex: 1, minWidth: 0, '& input': { fontSize: 13, fontWeight: 600, py: 0.25 } }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          onDoubleClick={() => writable && setEditingHeaderId(col.id)}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            cursor: writable ? 'text' : 'default',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            px: 0.5,
                          }}
                          title={writable ? '더블 클릭으로 이름 변경' : undefined}
                        >
                          {col.name || <em style={{ opacity: 0.5 }}>(이름 없음)</em>}
                        </Typography>
                      )}
                      {writable && (
                        <Tooltip title={`이 컬럼으로 행 정렬 (다시 누르면 역순). 품목 → 층 순서로 누르면 층별로 묶입니다.`}>
                          <IconButton
                            size="small"
                            onClick={() => handleSortByColumn(col.id)}
                            aria-label={`${col.name} 기준 정렬`}
                            sx={{
                              opacity: sort?.colId === col.id ? 1 : 0.4,
                              color: sort?.colId === col.id ? 'primary.main' : undefined,
                              '&:hover': { opacity: 1 },
                            }}
                          >
                            {sort?.colId === col.id && sort.dir === 'desc' ? (
                              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      {writable && (
                        <Tooltip title="컬럼 삭제">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteColumn(col.id)}
                            aria-label={`${col.name} 컬럼 삭제`}
                            sx={{ opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }}
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    {writable && (
                      <Box
                        onMouseDown={startResize(col.id, w)}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          handleResizeColumn(col.id, DEFAULT_COL_WIDTH);
                        }}
                        role="separator"
                        aria-label={`${col.name} 컬럼 폭 조절`}
                        aria-orientation="vertical"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          bottom: 0,
                          width: 6,
                          cursor: 'col-resize',
                          userSelect: 'none',
                          bgcolor: isResizing ? 'primary.main' : 'transparent',
                          opacity: isResizing ? 0.6 : 1,
                          transition: 'background-color 120ms',
                          '&:hover': { bgcolor: 'primary.main', opacity: 0.4 },
                          zIndex: 2,
                        }}
                      />
                    )}
                  </Box>
                );
              })}
              {writable && <Box component="th" sx={{ width: ACTION_COL_WIDTH, bgcolor: 'action.hover' }} />}
            </Box>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <Box component="tr">
                <Box
                  component="td"
                  colSpan={columns.length + (writable ? 2 : 1)}
                  sx={{ py: 3, textAlign: 'center', color: 'text.secondary', fontSize: 13 }}
                >
                  {writable ? '행이 없습니다. "행 추가" 또는 엑셀 업로드로 채우세요.' : '데이터가 없습니다.'}
                </Box>
              </Box>
            ) : (
              rows.map((row, rIdx) => (
                <Box
                  component="tr"
                  key={row.id}
                  sx={{ '&:hover': { bgcolor: 'action.hover' }, '&:hover .delete-row-btn': { opacity: 1 } }}
                >
                  <Box
                    component="td"
                    sx={{ py: 0, textAlign: 'center', color: 'text.secondary', fontSize: 11, userSelect: 'none' }}
                  >
                    {rIdx + 1}
                  </Box>
                  {columns.map((col) => {
                    const value = row.cells[col.id] ?? '';
                    if (!writable) {
                      return (
                        <Box component="td" key={col.id} sx={{ px: 1, py: 0.75, fontSize: 13 }}>
                          {value}
                        </Box>
                      );
                    }
                    return (
                      <Box component="td" key={col.id} sx={{ p: 0 }}>
                        <Box
                          component="input"
                          type="text"
                          value={value}
                          data-cell={`${row.id}__${col.id}`}
                          onChange={(e) => handleCellChange(row.id, col.id, e.target.value)}
                          onKeyDown={(e) =>
                            handleCellKeyDown(e as React.KeyboardEvent<HTMLInputElement>, rIdx, col.id)
                          }
                          sx={{
                            width: '100%',
                            border: 'none',
                            outline: 'none',
                            bgcolor: 'transparent',
                            color: 'text.primary',
                            fontSize: 13,
                            px: 1,
                            py: 0.75,
                            '&:focus': {
                              bgcolor: 'action.selected',
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                              outlineOffset: -2,
                            },
                          }}
                        />
                      </Box>
                    );
                  })}
                  {writable && (
                    <Box component="td" sx={{ textAlign: 'center' }}>
                      <Tooltip title="행 삭제">
                        <IconButton
                          className="delete-row-btn"
                          size="small"
                          onClick={() => {
                            if (window.confirm('이 행을 삭제할까요?')) handleDeleteRow(row.id);
                          }}
                          aria-label={`${rIdx + 1}행 삭제`}
                          sx={{ opacity: 0, transition: 'opacity 120ms', '&:hover': { color: 'error.main' } }}
                        >
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              ))
            )}
          </tbody>
        </Box>
      </Box>
    </Box>
  );
};

export default SpreadsheetGrid;
